import React, { useEffect, useState } from 'react';
import './styles';
import Geocode from 'react-geocode';
import moment from 'moment-timezone';
import PLTable from '../../../../../components/PL/PLTable/PLTable';
import { CloseIcon, ListIcon, MapIcon } from '../../../../../assets/icons';
import UILabeledInput from '../../../../../components/UI/UILabeledInput';
import UIButton from '../../../../../components/UI/UIButton/UIButton';
import PLMap from '../../../../../components/PL/PLMap';
import PLFilters from '../../../../../components/PL/PLFilters';
import UISegmentedControl from '../../../../../components/UI/UISegmentedControl';
import UISearchBar, { UI_SEARCH_BAR_TYPES } from '../../../../../components/UI/UISearchBar';
import { PHOTOSHOOT_TYPE_SERVICE } from '../../../../../utils/Config/Constants';
import UIController from '../../../../../components/UI/UIController';
import Helmet from 'react-helmet';
import { guessCurrentTimezone } from '../../../../../utils/Helpers/AppHelper';
import { SERVICES_TABLE_TYPE } from '../../../../../components/PL/PLTable/config';
import { UI_LABELED_INPUT_STATIC_TYPE } from '../../../../../components/UI/UILabeledInput/config';
import { useSelector } from 'react-redux';
import { getPaginatedTableVariablesByTypeAndSearchTypeScript } from '../../../../../components/PL/PLTable/TypeScriptHelpers';
import { getFiltersProp } from '../../../../../redux/selectors/CurrentUserSelectors';
import { useHistory, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/react-hooks';
import { GET_TECHNICIAN_SERVICES } from '../../../global/IndividualTechnician/IndividualTechnicianNetworkServices/queries';
import { PLFILTER_TYPE_SERVICE } from '../../../../../components/PL/PLFilters/config';
import UILoadingPage from '../../../../../components/UI/UILoadingPage/UILoadingPage';

let ReactMapboxGl, Layer, Feature, Popup;
if (process.env.BUILD_TARGET === 'client') {
	ReactMapboxGl = require('react-mapbox-gl');
	Layer = ReactMapboxGl.Layer;
	Feature = ReactMapboxGl.Feature;
	Popup = ReactMapboxGl.Popup;
}

Geocode.setApiKey(process.env.GOOGLE_MAPS ?? '');
Geocode.enableDebug();

const TABS = {
	LIST: { value: 'list', label: 'label' },
	MAP: { value: 'map', label: 'label' }
};

const QUERY_LIMIT = 15;

export default function AdminServices({}: AdminServicesProps) {
	const [services, setServices] = useState<ServiceType[]>();
	const [selectedTab, setSelectedTab] = useState<PLSegment>(TABS.LIST);
	const [selectedFeatureService, setSelectedFeatureService] = useState<ServiceType>();

	const history = useHistory();
	const location = useLocation();

	const filters = useSelector(getFiltersProp);
	let props = {
		filters,
		location
	};

	let baseVariables = { query: {}, isActive: true };
	let variables = getPaginatedTableVariablesByTypeAndSearchTypeScript(QUERY_LIMIT, SERVICES_TABLE_TYPE, props, baseVariables);
	const [getServices, { data: servicesData }] = useLazyQuery(GET_TECHNICIAN_SERVICES, {
		variables
	});

	useEffect(() => {
		(async () => {
			await getServices();
		})();
	}, []);

	useEffect(() => {
		(() => {
			if (servicesData) {
				const fetchedServices: ServiceType[] = servicesData['services'];
				setServices(fetchedServices);
			}
		})();
	}, [servicesData]);

	const renderTabComponent = () => {
		return (
			<div className='general-header-pin'>
				<PLFilters type={PLFILTER_TYPE_SERVICE} updateTable={getServices} />
				<UISegmentedControl
					style={{ marginRight: 12 }}
					segments={[
						{ value: 'list', icons: { mint: ListIcon.mint, grey: ListIcon.grey, white: ListIcon.white } },
						{ value: 'map', icons: { mint: MapIcon.mint, grey: MapIcon.grey, white: MapIcon.white } }
					]}
					selectedSegment={selectedTab}
					updateSelectedSegment={setSelectedTab}
				/>
				<UISearchBar type={UI_SEARCH_BAR_TYPES.service} onSearchUpdated={getServices} placeholder={'search by address..'} />
			</div>
		);
	};

	const renderContent = () => {
		if (!services) return <UILoadingPage />;
		if (selectedTab.value === TABS.LIST.value) {
			return (
				<PLTable
					type={SERVICES_TABLE_TYPE}
					limit={QUERY_LIMIT}
					emptySetText={'There are no services that match these filters!'}
					updateTable={getServices}
					data={services}
				/>
			);
		} else {
			const renderMapPopup = () => {
				if (selectedFeatureService) {
					return (
						<Popup
							style={{ zIndex: 10, borderRadius: 16, overflow: 'hidden' }}
							coordinates={[selectedFeatureService.property.longitude, selectedFeatureService.property.latitude]}
							offset={16}>
							<img alt={'close'} src={CloseIcon.white} onClick={() => setSelectedFeatureService(undefined)} className='popup-image' />
							<UILabeledInput
								containerStyle={{ marginTop: 8 }}
								type={UI_LABELED_INPUT_STATIC_TYPE}
								label={'ADDRESS'}
								value={selectedFeatureService.property.address}
							/>
							<UILabeledInput
								type={UI_LABELED_INPUT_STATIC_TYPE}
								label={'DATE'}
								value={moment(selectedFeatureService.serviceDate)
									.tz(selectedFeatureService.region ? selectedFeatureService.region.timezone : guessCurrentTimezone())
									.format('MM/DD/YYYY [at] hh:mm a z')}
							/>
							<UIButton
								className='popup-button'
								type={UIButton.OUTLINE_TYPE}
								onClick={() => {
									setSelectedFeatureService(undefined);
									history.push(location.pathname + `?service=${selectedFeatureService.id}`);
								}}
								text={'View service'}
							/>
						</Popup>
					);
				}
				return null;
			};
			return (
				<PLMap
					zoom={[9]}
					center={[services[0].property.longitude, services[0].property.latitude]}
					containerStyle={{
						height: '100%',
						backgroundColor: 'var(--secondary-background-color)',
						borderRadius: 16,
						margin: 8
					}}>
					<Layer type='symbol' id='marker' layout={{ 'icon-image': 'marker-15', 'icon-size': 1.6 }}>
						{services
							.filter((service) => service.type === PHOTOSHOOT_TYPE_SERVICE)
							.map((service) => {
								return (
									<Feature
										key={service.id}
										style={{ cursor: 'pointer' }}
										onClick={() => setSelectedFeatureService(service)}
										properties={{ minzoom: 0 }}
										coordinates={service.property ? [service.property.longitude, service.property.latitude] : [0, 0]}
									/>
								);
							})}
					</Layer>
					{renderMapPopup()}
				</PLMap>
			);
		}
	};

	return (
		<UIController bodyClassName={`managerServicesBody`}>
			<Helmet>
				<title>Services | Pineapple Labs</title>
				<meta property='og:title' content='Services | Pineapple Labs' />
			</Helmet>
			{renderTabComponent()}
			{renderContent()}
		</UIController>
	);
}
